import { useState, useEffect } from 'react';

const SESSION_TIMEOUT = 8 * 60 * 60 * 1000; // 8 horas en milisegundos
const ACTIVITY_CHECK_INTERVAL = 60000; // Verificar cada minuto

export const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true); // Estado de carga

  // Función para limpiar sesión
  const clearSession = () => {
    localStorage.removeItem('currentUser');
    localStorage.removeItem('sessionTimestamp');
    localStorage.removeItem('lastActivity');
    setIsAuthenticated(false);
    setUser(null);
  };

  // Función para verificar si la sesión es válida
  const isSessionValid = () => {
    const currentUser = localStorage.getItem('currentUser');
    const sessionTimestamp = localStorage.getItem('sessionTimestamp');
    const lastActivity = localStorage.getItem('lastActivity');

    if (!currentUser || !sessionTimestamp) {
      return false;
    }

    const now = Date.now();
    const sessionAge = now - parseInt(sessionTimestamp);
    const timeSinceLastActivity = now - parseInt(lastActivity || sessionTimestamp);

    // Sesión expirada si han pasado más de 8 horas o más de 2 horas de inactividad
    if (sessionAge > SESSION_TIMEOUT || timeSinceLastActivity > (2 * 60 * 60 * 1000)) {
      return false;
    }

    return true;
  };

  // Función para renovar actividad
  const renewActivity = () => {
    if (isAuthenticated) {
      localStorage.setItem('lastActivity', Date.now().toString());
    }
  };

  // Función para iniciar sesión
  const login = (userData) => {
    const now = Date.now();
    localStorage.setItem('currentUser', JSON.stringify(userData));
    localStorage.setItem('sessionTimestamp', now.toString());
    localStorage.setItem('lastActivity', now.toString());
    setUser(userData);
    setIsAuthenticated(true);
    setIsLoading(false);
  };

  // Función para cerrar sesión
  const logout = () => {
    clearSession();
    // Forzar recarga para ir al login
    window.location.href = '/login';
  };

  // Verificar sesión al cargar
  useEffect(() => {
    setIsLoading(true);
    
    if (isSessionValid()) {
      const userData = JSON.parse(localStorage.getItem('currentUser'));
      setIsAuthenticated(true);
      setUser(userData);
      renewActivity();
    } else {
      clearSession();
    }
    
    setIsLoading(false); // Termina la carga
  }, []);

  // Verificar sesión periódicamente
  useEffect(() => {
    const interval = setInterval(() => {
      if (isAuthenticated && !isSessionValid()) {
        alert('Tu sesión ha expirado por seguridad. Por favor, inicia sesión nuevamente.');
        logout();
      }
    }, ACTIVITY_CHECK_INTERVAL);

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Renovar actividad en eventos del usuario
  useEffect(() => {
    const handleActivity = () => {
      renewActivity();
    };

    if (isAuthenticated) {
      // Escuchar eventos de actividad del usuario
      window.addEventListener('mousedown', handleActivity);
      window.addEventListener('mousemove', handleActivity);
      window.addEventListener('keypress', handleActivity);
      window.addEventListener('scroll', handleActivity);
      window.addEventListener('touchstart', handleActivity);

      return () => {
        window.removeEventListener('mousedown', handleActivity);
        window.removeEventListener('mousemove', handleActivity);
        window.removeEventListener('keypress', handleActivity);
        window.removeEventListener('scroll', handleActivity);
        window.removeEventListener('touchstart', handleActivity);
      };
    }
  }, [isAuthenticated]);

  // Obtener información de sesión para debugging
  const getSessionInfo = () => {
    const sessionTimestamp = localStorage.getItem('sessionTimestamp');
    const lastActivity = localStorage.getItem('lastActivity');
    
    if (!sessionTimestamp) return null;
    
    const now = Date.now();
    const sessionAge = now - parseInt(sessionTimestamp);
    const timeSinceLastActivity = now - parseInt(lastActivity || sessionTimestamp);
    
    return {
      sessionAge: Math.floor(sessionAge / (1000 * 60)), // en minutos
      timeSinceLastActivity: Math.floor(timeSinceLastActivity / (1000 * 60)), // en minutos
      expiresIn: Math.floor((SESSION_TIMEOUT - sessionAge) / (1000 * 60)), // en minutos
    };
  };

  return {
    isAuthenticated,
    user,
    isLoading,
    login,
    logout,
    renewActivity,
    getSessionInfo,
    isSessionValid
  };
};